import { defineComponent as _defineComponent } from 'vue'
import { store } from '@/store';
import { StorageService } from '@/core';
import { SiteMode } from './site-mode';
import PageService from '@/core/page.service';
import CheckoutService from '@/services/checkout.service';
import PaymentProviderKlarna from '@/ui/views/checkout/PaymentProviderKlarna.vue';
import CheckoutPageViewModel = Vertica.LouisPoulsen.Application.Content.Pages.Checkout.CheckoutPageViewModel;
import SaveCheckoutWebRequest = Vertica.LouisPoulsen.Application.Commerce.Requests.Consumer.SaveCheckoutWebRequest;
import SaveCheckoutViewModel = Vertica.LouisPoulsen.Application.Commerce.ViewModels.Consumer.SaveCheckoutViewModel;
import CheckoutSummaryB2C from '@/ui/views/checkout/CheckoutSummaryB2C.vue';
import CalculateSalesTaxWebRequest = Vertica.LouisPoulsen.Application.Commerce.Requests.Consumer.CalculateSalesTaxWebRequest;
import CalculateSalesTaxResultViewModel = Vertica.LouisPoulsen.Application.Commerce.ViewModels.Consumer.CalculateSalesTaxResultViewModel;
import TaxTotalViewModel = Vertica.LouisPoulsen.Application.Commerce.ViewModels.Consumer.TaxTotalViewModel;
import TrackingService from '@/core/tracking.service';
import CustomerDetailsB2C from './CustomerDetailsB2C.vue';
import CustomerDetailsB2CJapan from './CustomerDetailsB2CJapan.vue';
import DeliveryMethodsB2C from './DeliveryMethodsB2C.vue';
import { useAddressAutoComplete } from '@/services/address-auto-complete.service';
import { useTranslations } from '@/core/translate.mixin';
import { ComponentOptions, computed, nextTick, onMounted, ref } from 'vue';
import { useValidator } from '@/core/app.service';
import { PaymentProvider } from '@/enums';
import { useNewsletter } from '@/store/newsletter.composable';

declare let Klarna: any;


export default /*#__PURE__*/_defineComponent({
  __name: 'CheckoutB2C',
  props: {
    model: null
  },
  setup(__props: any) {

const props = __props as { model: CheckoutPageViewModel }



const basket = computed<BasketViewModel>(() => store.getters['basket/basket']);
const isBasketLocked = computed<boolean>(() => store.getters['basket/isBasketLocked']);

const termsUrl = computed<string>(() => store.getters['app/termsUrl']);
const primaryCountryCode = computed<string>(() => store.getters['app/primaryCountryCode']);

const getKlarnaToken = computed<string>(() => store.getters['checkoutb2c/getKlarnaToken']);
const getKlarnaPaymentIdentifier = computed<string>(() => store.getters['checkoutb2c/getKlarnaPaymentIdentifier']);

function getCountryTaxTotal(countryCode: string) : Promise<TaxTotalViewModel> {
    return store.dispatch('checkoutb2c/getCountryTaxTotal', countryCode);
}
function submitOrder(request: SaveCheckoutWebRequest) : Promise<SaveCheckoutViewModel> {
    return store.dispatch('checkoutb2c/submitOrder', request);
}
function calculateSalesTax(request: CalculateSalesTaxWebRequest) : Promise<CalculateSalesTaxResultViewModel> {
    return store.dispatch('basket/calculateSalesTax', request);
}

const { privacyPolicyStatementHtml, newsletterFlagText, profilingFlagText } = useNewsletter();

const labelNamespace = 'Pages.Checkout';
const customer = ref<InvoiceAddress>({
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    company: '',
    address: '',
    postalCode: '',
    city: '',
    countryCode: '',
    extendedFields: {
        stateCode: '',
        address2: ''
    }
});

// Edited through DeliveryMethodsB2C
const deliveryInfo = ref<ShippingAddress>({
    phone: '',
    firstName: '',
    lastName: '',
    address: '',
    postalCode: '',
    city: '',
    countryCode: '',
    extendedFields: {
        stateCode: '',
        address2: ''
    },
    company: ''
});

const selectedDeliveryMethod = ref<string>('');
const selectedPickupShopIdValue = ref<string>('');
const selectedPickupShopValue: any = ref<any>({});
let paymentForm: ComponentOptions<any> = {
    template: '<div></div>'
};

const loadPickups = ref<boolean>(false);
const $validator = useValidator();

const alternativeDelivery = ref<boolean>(false);
const comment = ref<string>('');
const hasAcceptedTerm = ref<boolean>(false);

const selectedCustomerCountry = ref<InvoiceAddressCountryViewObject>({} as InvoiceAddressCountryViewObject);
const selectedShippingCountry = ref<AddressCountryViewObject>({} as AddressCountryViewObject);

const selectedPaymentProviderValue = ref<string>(PaymentProvider.Dibs.toString());

const authorizationToken = ref<string>(null);
const taxCalculationErrorMessage = ref<string>(null);
const countryTaxTotal = ref<string>(null);

const marketingTermsAccepted = ref<boolean>(false);
const profilingFlagAccepted = ref<boolean>(false);
const submittedMarketingEmailPermission = ref<boolean>(false);

const paymentMethods = computed<Vertica.LouisPoulsen.Application.Content.Pages.Checkout.PaymentMethodViewObject[]>(() => {
    let paymentMethods = props.model.paymentMethods;

    if (!isInvoiceCountryPrimaryCountry.value) {
        const klarnaPaymentMethod = paymentMethods.find(x => x.paymentProvider === PaymentProvider.Klarna as number);

        paymentMethods = paymentMethods.filter(obj => obj !== klarnaPaymentMethod);
    }

    return paymentMethods;
});

const mandatoryAlternativeDelivery = computed<boolean>(() => {
    return props.model.shippingAddressCountries.map(country => country.countryCode).indexOf(selectedCustomerCountry.value.id) < 0;
});

const isInvoiceCountryPrimaryCountry = computed<boolean>(() => {
    return customer.value.countryCode === primaryCountryCode.value;
});

const orderRequest = computed<SaveCheckoutWebRequest>(() => {
    return {
        customer: customer.value,
        newsletterFlagAccepted: marketingTermsAccepted.value,
        profilingFlagAccepted: profilingFlagAccepted.value,
        alternativeDelivery: deliveryInfo.value,
        deliveryMethod: selectedDeliveryMethod.value,
        pickupShop: selectedPickupShopIdValue.value,
        useAlternativeDeliveryAddress: alternativeDelivery.value,
        comment: comment.value,
        paymentProvider: selectedPaymentProvider.value,
        klarnaToken: selectedPaymentProvider.value === PaymentProvider.Klarna ? getKlarnaToken.value : null,
        klarnaAuthorizationToken: selectedPaymentProvider.value === PaymentProvider.Klarna ? authorizationToken.value : null,
        klarnaPaymentMethod: selectedPaymentProvider.value === PaymentProvider.Klarna ? getKlarnaPaymentIdentifier.value : null
    };
});

const selectedPaymentProvider = computed<number>(() => {
    return parseInt(selectedPaymentProviderValue.value, 10);
});

const { t } = useTranslations();

onMounted(() => {
    const dictionary = {
        custom: {
            terms: {
                required: t(`${labelNamespace}.TermsRequiredErrorMessage`)
            }
        }
    };
    $validator.localize('en', dictionary);
    if (props.model.isJapanMarket) {
        setUpAutocompleteAddressForJapanMarket();
    }

    fetchFormData();
    comment.value = props.model.comment;

    if (props.model.paymentProvider) { selectedPaymentProviderValue.value = props.model.paymentProvider.toString(); }

    nextTick(() => { updateTaxTotalForCountry(); });

    if (!props.model.hasSubmittedMarketingEmailPermission) {
        TrackingService.event('Newsletter in checkout', 'page2', 'CheckoutNudge');
    }
});

const { script, yubinBangoLoaded, setUpAutocompleteAddressForJapanMarket } = useAddressAutoComplete();

async function updateShippingCountry(country : AddressCountryViewObject) {
    selectedShippingCountry.value = country;
    await updateTaxTotalForCountry();
}

function setAlternativeDelivery(alternativeDeliveryValue: boolean) {
    alternativeDelivery.value = alternativeDeliveryValue;
}

function validatePickupShops() {
    const hasPickup = hasPickupShops();
    if (!selectedPickupShopValue.value && !selectedPickupShopIdValue.value && hasPickup) {
        const firstInvalidElement = $('#deliverymethods').first()[0];
        if (firstInvalidElement) {
            firstInvalidElement.scrollIntoView({ behavior: 'smooth' });
        }
        return false;
    }
    return true;
}

function hasPickupShops() {
    const currentDeliveryMethod = props.model.deliveryMethods.find(d => d.id === selectedDeliveryMethod.value);
    if (currentDeliveryMethod) {
        return currentDeliveryMethod.loadGlsPickupShops;
    }
    return false;
}

function setDeliveryMethod(deliveryMethodId: string) {
    selectedDeliveryMethod.value = deliveryMethodId;
}

function setSelectedPickupShop(selectedPickupShop: any, selectedPickupShopId: string) {
    selectedPickupShopValue.value = selectedPickupShop;
    selectedPickupShopIdValue.value = selectedPickupShopId;
}

function onCustomerCountryChanged(country: InvoiceAddressCountryViewObject) {
    if (country !== undefined) {
        selectedCustomerCountry.value = country;
        customer.value.countryCode = country.id;
    }

    setCustomerInLocalStorage(CheckoutService.customerLocalStorageKey, customer.value);

    if (!isInvoiceCountryPrimaryCountry.value) {
        if (selectedPaymentProvider.value === PaymentProvider.Klarna) {
            selectedPaymentProviderValue.value = PaymentProvider.Adyen.toString();
        }
    }

    updateTaxTotalForCountry();
}

function validateAndProcessOrder() {
    $validator
        .validateAll()
        .then(isValid => {
            if (isValid) {
                const paymentProvider = selectedPaymentProvider.value;
                // DIBS
                if (paymentProvider === PaymentProvider.Dibs) {
                    submitOrder(orderRequest.value).then(response => {
                        paymentForm = generatePaymentFormComponent(response.paymentHtmlForm);
                    });
                }
                // Klarna
                if (paymentProvider === PaymentProvider.Klarna) {
                    if (!validatePickupShops()) { return; }
                    authorizeKlarnaOrder();
                }

                // Ayden
                if (paymentProvider === PaymentProvider.Adyen) {
                    submitOrder(orderRequest.value).then(response => {
                        PageService.softRedirect(response.redirectUrl);
                    });
                }

                // Paypal
                if (paymentProvider === PaymentProvider.Paypal) {
                    submitOrder(orderRequest.value).then(response => {
                        PageService.softRedirect(response.redirectUrl);
                    });
                }
            } else {
                scrollInvalidElementIntoView();
            }
        })
        .catch(x => console.log('catch' + x));
}

function authorizeKlarnaOrder() {
    const klarnaPaymentMethod = {
        payment_method_category: getKlarnaPaymentIdentifier.value
    };
    const klarnaCustomerInfo = {
        billing_address: {
            given_name: customer.value.firstName,
            family_name: customer.value.lastName,
            email: customer.value.email,
            street_address: customer.value.address,
            postal_code: customer.value.postalCode,
            city: customer.value.city,
            phone: customer.value.phone,
            country: customer.value.countryCode
        },
        shipping_address: {
            given_name: customer.value.firstName,
            family_name: customer.value.lastName,
            email: customer.value.email,
            street_address: customer.value.address,
            postal_code: customer.value.postalCode,
            city: customer.value.city,
            phone: customer.value.phone,
            country: customer.value.countryCode
        }
    };

    if (alternativeDelivery.value) {
        klarnaCustomerInfo.shipping_address = {
            given_name: deliveryInfo.value.firstName,
            family_name: deliveryInfo.value.lastName,
            email: customer.value.email,
            street_address: deliveryInfo.value.address,
            postal_code: deliveryInfo.value.postalCode,
            city: deliveryInfo.value.city,
            phone: deliveryInfo.value.phone,
            country: customer.value.countryCode
        };
    }

    Klarna.Payments.authorize(klarnaPaymentMethod, klarnaCustomerInfo,
        (result: any) => {
            if (result.approved) {
                authorizationToken.value = result.authorization_token;
                submitOrder(orderRequest.value).then(response => {
                    if (response.redirectUrl) {
                        window.location.href = response.redirectUrl;
                    }
                });
            }
        });
}

function setCustomerInLocalStorage(key, value) {
    StorageService.setItemAs(key, value);
}

function fetchFormData() {
    // Customer form data
    customer.value = getCustomerData();
    // Alternative delivery address form data
    deliveryInfo.value = getDeliveryInfoData();
}

function getCustomerData(): InvoiceAddress {
    // Get from local storage
    const customer:InvoiceAddress = StorageService.getItemAs(CheckoutService.customerLocalStorageKey);

    // If no storage then return server object
    if (!customer) {
        return {
            email: props.model.customer.email || props.model.marketingEmailPermissionEmail,
            phone: props.model.customer.phone,
            firstName: props.model.customer.firstName,
            lastName: props.model.customer.lastName,
            company: props.model.customer.company,
            address: props.model.customer.address,
            postalCode: props.model.customer.postalCode,
            city: props.model.customer.city,
            countryCode: props.model.customer.country.countryCode,
            extendedFields: {
                stateCode: props.model.customer.stateCode,
                address2: props.model.customer.address2
            }
        };
    }

    return {
        email: customer.email || props.model.marketingEmailPermissionEmail,
        phone: customer.phone,
        firstName: customer.firstName,
        lastName: customer.lastName,
        company: customer.company,
        address: customer.address,
        postalCode: customer.postalCode,
        city: customer.city,
        countryCode: customer.countryCode || props.model.customer.country.countryCode,
        extendedFields: {
            stateCode: customer.extendedFields ? customer.extendedFields.stateCode : '',
            address2: customer.extendedFields ? customer.extendedFields.address2 : ''
        }
    };
}

async function updateTaxTotalForCountry() {
    if (props.model.isUsMarket) { return; }

    let countryCode = null;

    if (!alternativeDelivery.value && customer.value.countryCode) {
        countryCode = customer.value.countryCode;
    }

    if (alternativeDelivery.value && selectedShippingCountry.value.countryCode) {
        countryCode = selectedShippingCountry.value.countryCode;
    }

    if (!countryCode) {
        countryTaxTotal.value = null;
        return;
    }

    countryTaxTotal.value = (await getCountryTaxTotal(countryCode)).taxTotal;
}

function getDeliveryInfoData(): ShippingAddress {
    // Get from local storage
    const deliveryInfoValue:ShippingAddress = StorageService.getItemAs(CheckoutService.deliveryInfoLocalStorageKey);

    // If no storage then return server object
    if (!deliveryInfoValue) {
        return {
            phone: props.model.alternativeDelivery.phone,
            firstName: props.model.alternativeDelivery.firstName,
            lastName: props.model.alternativeDelivery.lastName,
            address: props.model.alternativeDelivery.address,
            postalCode: props.model.alternativeDelivery.postalCode,
            city: props.model.alternativeDelivery.city,
            countryCode: props.model.alternativeDelivery.country.countryCode,
            extendedFields: {
                address2: props.model.alternativeDelivery.address2,
                stateCode: props.model.alternativeDelivery.stateCode
            },
            company: props.model.alternativeDelivery.company
        };
    }

    return {
        phone: deliveryInfoValue.phone,
        firstName: deliveryInfoValue.firstName,
        lastName: deliveryInfoValue.lastName,
        address: deliveryInfoValue.address,
        postalCode: deliveryInfoValue.postalCode,
        city: deliveryInfoValue.city,
        countryCode: deliveryInfoValue.countryCode,
        extendedFields: deliveryInfoValue.extendedFields,
        company: deliveryInfoValue.company
    };
}

function generatePaymentFormComponent(htmlTemplate: string): ComponentOptions<any> {
    return {
        template: `<div>${htmlTemplate}</div>`,
        mounted() {
            document.querySelector<HTMLFormElement>('form[name=\'dibsForm\']').submit();
        }
    };
}

function scrollInvalidElementIntoView() {
    const firstInvalidElement = document.querySelector('.is-danger');
    if (firstInvalidElement) {
        firstInvalidElement.scrollIntoView({ behavior: 'smooth' });
    }
}

function onTaxFieldChange() {
    if (props.model.isUsMarket) {
        const city = alternativeDelivery.value ? deliveryInfo.value.city : customer.value.city;
        const postalCode = alternativeDelivery.value ? deliveryInfo.value.postalCode : customer.value.postalCode;
        const stateCode = alternativeDelivery.value ? deliveryInfo.value.extendedFields.stateCode : customer.value.extendedFields.stateCode;

        const allFieldsArePresent = city && postalCode && stateCode;

        if (allFieldsArePresent) {
            const request: CalculateSalesTaxWebRequest = {
                useAlternativeDeliveryAddress: alternativeDelivery.value,
                stateCode,
                city,
                postalCode
            };

            taxCalculationErrorMessage.value = null;
            calculateSalesTax(request).then((response : CalculateSalesTaxResultViewModel) => {
                if (!response.success) {
                    taxCalculationErrorMessage.value = response.errorMessage;
                } else if (!alternativeDelivery.value) {
                    $validator.validate('salesTaxValidation');
                } else if (alternativeDelivery.value) {
                    $validator.validate('salesTaxValidation_');
                }
            });
        }
    }
}

function shouldLoadPickups() {
    loadPickups.value = !loadPickups.value;
}

const siteMode = SiteMode;


return { __sfc: true,props, basket, isBasketLocked, termsUrl, primaryCountryCode, getKlarnaToken, getKlarnaPaymentIdentifier, getCountryTaxTotal, submitOrder, calculateSalesTax, privacyPolicyStatementHtml, newsletterFlagText, profilingFlagText, labelNamespace, customer, deliveryInfo, selectedDeliveryMethod, selectedPickupShopIdValue, selectedPickupShopValue, paymentForm, loadPickups, $validator, alternativeDelivery, comment, hasAcceptedTerm, selectedCustomerCountry, selectedShippingCountry, selectedPaymentProviderValue, authorizationToken, taxCalculationErrorMessage, countryTaxTotal, marketingTermsAccepted, profilingFlagAccepted, submittedMarketingEmailPermission, paymentMethods, mandatoryAlternativeDelivery, isInvoiceCountryPrimaryCountry, orderRequest, selectedPaymentProvider, t, script, yubinBangoLoaded, setUpAutocompleteAddressForJapanMarket, updateShippingCountry, setAlternativeDelivery, validatePickupShops, hasPickupShops, setDeliveryMethod, setSelectedPickupShop, onCustomerCountryChanged, validateAndProcessOrder, authorizeKlarnaOrder, setCustomerInLocalStorage, fetchFormData, getCustomerData, updateTaxTotalForCountry, getDeliveryInfoData, generatePaymentFormComponent, scrollInvalidElementIntoView, onTaxFieldChange, shouldLoadPickups, siteMode, PaymentProviderKlarna, CheckoutSummaryB2C, CustomerDetailsB2C, CustomerDetailsB2CJapan, DeliveryMethodsB2C, PaymentProvider }
}

})